import React, { useState, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  staticDataTitlesMapper,
  staticDataSpecialtiesMapper,
  staticDataCountriesMapper,
  staticDataNationalitiesMapper,
  staticDataEntityTypesMapper,
} from 'utils/functions/objectMapper';
import { trimText } from 'utils/functions/general';
import { getDoctorUserInfo } from 'modules/doctors/api';
import { doctorStatusEnum, doctorStatusColorEnum } from 'modules/doctors/enums';
import doctorsStrings from 'modules/doctors/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  LoaderWrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemRow,
  ItemWrapper,
  ItemTextWrapper,
  ItemText,
  ItemTextBold,
  ItemSubText,
  ItemClick,
  ItemTag,
  /* ####################### */
  ItemBtn,
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
  /* ####################### */
  BadgesPopupWrapper,
  BadgesItemWrapper,
  BadgesText,
  BadgesAssignBtn,
  BadgesRevokeBtn,
  BadgesLoaderWrapper,
} from './DoctorsList.style';

const DoctorActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {doctorsStrings.doctorsList.actionPopupTitle(actionStatus)}
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {doctorsStrings.doctorsList.actionPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {doctorsStrings.doctorsList.actionPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
DoctorActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionStatus: PropTypes.string.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const DoctorBadgesPopup = ({
  staticData,
  themeContext,
  isBadgePopupLoading,
  badgeTarget,
  loadedDoctorsBadges,
  handleBadgePopupConfirmBtnClick,
  handleBadgePopupDismissBtnClick,
}) => {
  const { details: staticDetails } = staticData;
  const { badges } = staticDetails;

  const doctorBadges = loadedDoctorsBadges[badgeTarget];

  return (
    <Popup handleCloseBtnClick={() => handleBadgePopupDismissBtnClick()}>
      <BadgesPopupWrapper>
        {badges.map((badge, index) => {
          const isLastItem = index === badges.length - 1;

          return (
            <React.Fragment key={badge.badgeKey}>
              <BadgesItemWrapper>
                <BadgesText>{badge.languageDetails[0].name}</BadgesText>
                {doctorBadges?.includes(badge.badgeKey) === false && (
                  <BadgesAssignBtn
                    as="button"
                    onClick={() => handleBadgePopupConfirmBtnClick(badge.badgeKey, true)}
                  >
                    {doctorsStrings.doctorsList.badgePopupAssignBtn}
                  </BadgesAssignBtn>
                )}
                {doctorBadges?.includes(badge.badgeKey) === true && (
                  <BadgesRevokeBtn
                    as="button"
                    onClick={() => handleBadgePopupConfirmBtnClick(badge.badgeKey, false)}
                  >
                    {doctorsStrings.doctorsList.badgePopupRevokeBtn}
                  </BadgesRevokeBtn>
                )}
              </BadgesItemWrapper>
              {isLastItem === false && (
                <SectionDivider
                  gap={16}
                  withSeparator
                  separatorGap={16}
                  borderColor={themeContext.colors.grey004}
                />
              )}
            </React.Fragment>
          );
        })}
        {isBadgePopupLoading && (
          <>
            <SectionDivider gap={30} />
            <BadgesLoaderWrapper>
              <Loader width={28} height={28} thickness={4} />
            </BadgesLoaderWrapper>
          </>
        )}
      </BadgesPopupWrapper>
    </Popup>
  );
};
DoctorBadgesPopup.propTypes = {
  staticData: PropTypes.shape({
    details: PropTypes.shape({
      badges: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isBadgePopupLoading: PropTypes.bool.isRequired,
  badgeTarget: PropTypes.string.isRequired,
  loadedDoctorsBadges: PropTypes.shape({}).isRequired,
  handleBadgePopupConfirmBtnClick: PropTypes.func.isRequired,
  handleBadgePopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const DoctorNumber = ({ doctorKey, themeContext }) => {
  const [showLoader, setShowLoader] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');

  const handleShowDoctorNumberClick = () => {
    const body = {
      userKey: doctorKey,
    };

    setShowLoader(true);

    getDoctorUserInfo(body)
      .then(res => {
        setMobileNumber(`${res.data.countryCode} ${res.data.phoneNum}`);
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
      });
  };

  if (showLoader)
    return (
      <LoaderWrapper>
        <Loader width={30} height={30} thickness={4} />
      </LoaderWrapper>
    );

  if (mobileNumber) return <ItemText>{mobileNumber}</ItemText>;

  return (
    <ItemBtn
      as="button"
      color={themeContext.colors.primaryMain}
      backgroundColor={themeContext.colors.white}
      borderColor={themeContext.colors.primaryMain}
      onClick={handleShowDoctorNumberClick}
    >
      {doctorsStrings.doctorsList.showNumberText}
    </ItemBtn>
  );
};
DoctorNumber.propTypes = {
  doctorKey: PropTypes.string.isRequired,
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      primaryMain: PropTypes.string,
      white: PropTypes.string,
    }),
  }).isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const DoctorsList = ({
  doctorsListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  isBadgePopupOpen,
  isBadgePopupLoading,
  badgePopupTarget,
  loadedDoctorsBadges,
  handleTextToCopyToClipboardClick,
  handlePreviewFileClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
  handleBadgeBtnClick,
  handleBadgePopupConfirmBtnClick,
  handleBadgePopupDismissBtnClick,
}) => {
  const { staticData } = useSelector(state => ({
    staticData: state.common.staticData,
  }));
  const { details: staticDetails } = staticData;

  const mappedTitles = useMemo(
    () => staticDataTitlesMapper(staticDetails.doctorTitles),
    [staticDetails],
  );
  const mappedSpecialties = useMemo(
    () => staticDataSpecialtiesMapper(staticDetails.specialties),
    [staticDetails],
  );
  const mappedCountries = useMemo(
    () => staticDataCountriesMapper(staticDetails.countries),
    [staticDetails],
  );
  const mappedNationalities = useMemo(
    () => staticDataNationalitiesMapper(staticDetails.nationalities),
    [staticDetails],
  );
  const mappedEntityTypes = useMemo(
    () => staticDataEntityTypesMapper(staticDetails.entityTypes),
    [staticDetails],
  );

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const doctorsGridHeaderList = doctorsStrings.doctorsList.doctorsGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(doctorsGridHeaderList).map(doctorsGridHeaderKey => (
            <Header key={doctorsGridHeaderKey}>
              {doctorsGridHeaderList[doctorsGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {doctorsListArr.map((doctor, index) => {
            const doctorStatusId = doctor.licenceStatus.licenceStatusId;

            const doctorStatusColor = doctorStatusColorEnum(colors)[doctorStatusId].color;
            const doctorStatusBackgroundColor =
              doctorStatusColorEnum(colors)[doctorStatusId].backgroundColor;

            const isLastItem = index === doctorsListArr.length - 1;

            return (
              <React.Fragment key={doctor.userKey}>
                <ItemColumn>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(doctor.userKey)}>
                    {trimText(doctor.userKey, 10)}
                  </ItemClick>
                </ItemColumn>
                <ItemColumn>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(doctor.profileKey)}>
                    {trimText(doctor.profileKey, 10)}
                  </ItemClick>
                </ItemColumn>
                <ItemColumn>
                  <ItemWrapper>
                    <ItemTextWrapper
                      backgroundColor={doctorStatusBackgroundColor}
                      borderColor={doctorStatusColor}
                    >
                      <ItemText color={doctorStatusColor} inline>
                        {mappedTitles[doctor.doctorTitleKey]}
                      </ItemText>{' '}
                      <ItemTextBold color={doctorStatusColor} inline>
                        {doctor.fullName}
                      </ItemTextBold>
                    </ItemTextWrapper>
                    <SectionDivider gap={2} />
                    <ItemSubText>{doctor.email}</ItemSubText>
                  </ItemWrapper>
                </ItemColumn>
                <ItemRow>
                  <DoctorNumber doctorKey={doctor.userKey} themeContext={themeContext} />
                </ItemRow>
                <ItemColumn>
                  <ItemText>{mappedSpecialties[doctor.specialtyKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{mappedCountries[doctor.countryKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{mappedNationalities[doctor.nationalityKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemWrapper>
                    <ItemText>{doctor.entityName}</ItemText>
                    <SectionDivider gap={2} />
                    <ItemSubText>{mappedEntityTypes[doctor.entityTypeKey]}</ItemSubText>
                  </ItemWrapper>
                </ItemColumn>
                <ItemColumn>
                  <ItemTag hover onClick={() => handlePreviewFileClick(true, doctor.licences[0])}>
                    {doctorsStrings.doctorsList.doctorsLicenceGridItem}
                  </ItemTag>
                </ItemColumn>
                <ItemRow>
                  {(doctorStatusId === doctorStatusEnum.Pending ||
                    doctorStatusId === doctorStatusEnum.Rejected) && (
                    <ItemBtn
                      as="button"
                      onClick={
                        () =>
                          handleActionBtnClick(doctor.profileKey, doctorStatusEnum.getKeyByValue(2)) // 2 is Verified
                      }
                    >
                      {doctorsStrings.doctorsList.doctorsGridVerifyActionBtn}
                    </ItemBtn>
                  )}
                  {(doctorStatusId === doctorStatusEnum.Pending ||
                    doctorStatusId === doctorStatusEnum.Verified) && (
                    <ItemBtn
                      as="button"
                      onClick={
                        () =>
                          handleActionBtnClick(doctor.profileKey, doctorStatusEnum.getKeyByValue(3)) // 3 is Rejected
                      }
                    >
                      {doctorsStrings.doctorsList.doctorsGridRejectActionBtn}
                    </ItemBtn>
                  )}
                  {doctorStatusId === doctorStatusEnum.Verified && (
                    <ItemBtn as="button" onClick={() => handleBadgeBtnClick(doctor.profileKey)}>
                      {doctorsStrings.doctorsList.doctorsGridAddBadgeActionBtn}
                    </ItemBtn>
                  )}
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <DoctorActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
      {isBadgePopupOpen && (
        <DoctorBadgesPopup
          staticData={staticData}
          themeContext={themeContext}
          isBadgePopupLoading={isBadgePopupLoading}
          badgeTarget={badgePopupTarget}
          loadedDoctorsBadges={loadedDoctorsBadges}
          handleBadgePopupConfirmBtnClick={handleBadgePopupConfirmBtnClick}
          handleBadgePopupDismissBtnClick={handleBadgePopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

DoctorsList.propTypes = {
  doctorsListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.string.isRequired,
  isBadgePopupOpen: PropTypes.bool.isRequired,
  isBadgePopupLoading: PropTypes.bool.isRequired,
  badgePopupTarget: PropTypes.string.isRequired,
  loadedDoctorsBadges: PropTypes.shape({}).isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
  handleBadgeBtnClick: PropTypes.func.isRequired,
  handleBadgePopupConfirmBtnClick: PropTypes.func.isRequired,
  handleBadgePopupDismissBtnClick: PropTypes.func.isRequired,
};

DoctorsList.defaultProps = {};

export default DoctorsList;
